import { Accessor, SeriesPoint } from '@visx/shape/lib/types';
import { ScaleBand, ScaleLinear, ScaleOrdinal, ScaleTime } from 'd3-scale';
import { ReactNode } from 'react';

export interface MarkPalette {
    default: string;
    hover: string;
    blur: string;
    active: string | string[];
}

export interface GridPalette {
    major: string;
    minor: string;
}

export interface AxesPalette {
    x: string;
    y: string;
}

export interface Palette {
    background: string;
    text: string;
    grid: string | GridPalette;
    axes: string | AxesPalette;
    marks: MarkPalette;
}

export interface MarkProps {
    data: any[];
    colorAccessor: (key: string) => string;
}

export interface Record {
    key: string;
    group: string;
    label: string | number | Date;
    data: number;
}

export interface AxisProps {
    label?: string;
    interval?: 'day' | 'week' | 'month' | 'quarter' | 'year' | number;
    isDate?: boolean;
}

export interface AxesProps {
    top?: AxisProps;
    bottom?: AxisProps;
    left?: AxisProps;
    right?: AxisProps;
    layout?: ChartLayoutProps;
}

export interface SingleSeries {
    key: string | number;
    data: number;
}

export interface MultiSeries {
    key: string | number;
    label?: string;
    data: SingleSeries[];
}

export type DataSeries = SingleSeries[] | MultiSeries[];

export interface TooltipProps {
    bar?: SeriesPoint<any>;
    value?: number;
    key: string;
    index: number;
    height: number;
    width: number;
    x: number;
    y: number;
    color: string;
}

export interface MarginProps {
    top: number;
    left: number;
    bottom: number;
    right: number;
}

export interface ScaleProps {
    x: ScaleBand<any> | ScaleTime<any, any> | ScaleLinear<any, any>;
    x0?: ScaleBand<any>;
    x1?: ScaleBand<any>;
    y: ScaleLinear<any, any>;
    color: ScaleOrdinal<any, any>;
}

export interface OffsetProps {
    x?: number;
    y?: number;
}

export interface RangeProps {
    domain: number[] | string[] | undefined;
    range: [number, number];
}

export interface BoundsProps {
    x: RangeProps;
    y: RangeProps;
}

export interface AxisTickFormatProps {
    top?: Accessor<any, any>;
    left?: Accessor<any, any>;
    bottom?: Accessor<any, any>;
    right?: Accessor<any, any>;
}

export interface FormatProps {
    axisTick?: AxisTickFormatProps;
}

export interface AccessorProps {
    x?: Accessor<any, any>;
    x0?: Accessor<any, any>;
    x1?: Accessor<any, any>;
    y?: Accessor<any, any>;
    y0?: Accessor<any, any>;
    y1?: Accessor<any, any>;
}

export type ChartLayoutProps = {
    margin: MarginProps;
    scale: ScaleProps;
    bounds: BoundsProps;
    offset?: OffsetProps;
    format?: FormatProps;
    accessor?: AccessorProps;
};

export enum ChartTextSize {
    Small = 'small',
    Medium = 'medium',
    Large = 'large',
}

export interface ChartAxisFontSizes {
    x: number;
    y: number;
    yLabel: number;
    legend: number;
    description: number;
    title: number;
}

export const CHART_TEXT_SIZE_CONFIG: { [key in ChartTextSize]: ChartAxisFontSizes } = {
    [ChartTextSize.Small]: { x: 11, y: 11, yLabel: 11, legend: 12, description: 11, title: 14 },
    [ChartTextSize.Medium]: { x: 14, y: 14, yLabel: 14, legend: 16, description: 14, title: 18 },
    [ChartTextSize.Large]: { x: 20, y: 18, yLabel: 20, legend: 20, description: 18, title: 22 },
};

export interface ChartProps {
    title?: string;
    description?: string;
    actions?: ReactNode[];
    markOptions: string[];
    activeMarkIndex: number;
    width: number;
    height: number;
    data: DataSeries;
    noDataImgSrc?: string;
    error?: string;
    errorImgSrc?: string;
    palette: string[];
    axes?: AxesProps;
    margin?: MarginProps;
    domainY?: [number, number];
    legendValues?: string[];
    formatLegendLabel?: (label: string) => string;
    groupAccessor?: (key: string) => string;
    xLines?: { value: number; color: string }[];
    yLines?: { value: number; color: string }[];
    textSize?: ChartTextSize;
}
